/**
 * Papel de regalo — Las Cosas de Oli
 * Compatible con Horizon 4.1.4 (dos rutas de alta al carrito).
 *
 * El bloque `blocks/gift-wrap.liquid` pinta el checkbox #gift-wrap-checkbox.
 * Cuando está marcado, interceptamos la petición a /cart/add y añadimos
 * el producto del envoltorio como un ítem más, con la misma cantidad
 * que el producto principal.
 *
 * Qué envoltorio: el bloque publica el id de variante en
 * `data-wrap-variant-id` del propio checkbox (sale del ajuste de tema
 * `wrap_product`). Si ese atributo no está —bloque sin configurar—
 * caemos al comportamiento histórico: resolver la variante pidiendo
 * /products/<WRAP_HANDLE>.js.
 *
 * Rutas cubiertas (verificadas en assets/product-form.js de este tema):
 *   1) Alta individual  -> body FormData      (fetchConfig('javascript', { body: formData }))
 *   2) Alta múltiple    -> body string JSON   ({ items: [...], sections: '...' })
 *
 * Principio rector: un fallo del envoltorio nunca debe impedir una venta.
 * Cuando no entendemos la petición NO la bloqueamos: la dejamos pasar tal cual
 * y añadimos el envoltorio en una segunda petición independiente (plan B).
 *
 * Depuración: añade `?lco_debug=1` a la URL para ver en consola, con el
 * prefijo [gift-wrap], por qué el parche ha hecho lo que ha hecho.
 *
 * El script se carga desde `layout/theme.liquid` (no desde el bloque): un
 * <script> dentro de un bloque no se ejecuta cuando la sección se reinyecta
 * por la Section Rendering API con `morph` (nodos creados con DOMParser).
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Depuración
   * ------------------------------------------------------------------ */

  var DEBUG = /[?&]lco_debug=1/.test(location.search);
  function debug() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[gift-wrap]'].concat([].slice.call(arguments)));
    }
  }

  /* ------------------------------------------------------------------ *
   * Configuración
   * ------------------------------------------------------------------ */

  // Reserva: sólo se usa si el bloque no trae `data-wrap-variant-id`.
  var WRAP_HANDLE = 'envoltorio-regalo';
  var CHECKBOX_SELECTOR = '#gift-wrap-checkbox';
  var QUANTITY_SELECTOR = 'form[data-type="add-to-cart-form"] input[name="quantity"]';
  var CART_ADD_MARKER = '/cart/add';
  var LOOP_FLAG = 'gift_wrap_added';

  /* ------------------------------------------------------------------ *
   * Guarda anti doble-parcheo
   * ------------------------------------------------------------------ */

  if (window.__lcoGiftWrapPatched) {
    debug('parche ya instalado: no se vuelve a instalar');
    return;
  }
  if (typeof window.fetch !== 'function') {
    debug('este navegador no tiene window.fetch: no se instala el parche');
    return;
  }
  window.__lcoGiftWrapPatched = true;

  var originalFetch = window.fetch.bind(window);
  window.__lcoGiftWrapOriginalFetch = originalFetch;

  function warn(message) {
    if (window.console && typeof console.warn === 'function') {
      console.warn('[gift-wrap]', message);
    }
  }

  /* ------------------------------------------------------------------ *
   * Variante del envoltorio
   * ------------------------------------------------------------------ */

  // Sólo cachea el id resuelto POR HANDLE. El id que viene del bloque no se
  // guarda nunca aquí: puede cambiar de una plantilla a otra y se relee en
  // cada alta al carrito.
  var wrapVariantId = null;
  var wrapVariantRequest = null;

  /**
   * Id de variante publicado por el bloque en el propio checkbox.
   * Se lee en cada alta al carrito (nunca se cachea) y evita la petición
   * de red a /products/<handle>.js.
   */
  function readWrapVariantIdFromBlock(checkbox) {
    if (!checkbox || typeof checkbox.getAttribute !== 'function') return null;

    var raw = checkbox.getAttribute('data-wrap-variant-id');
    if (!raw) return null;

    var parsed = toPositiveInt(raw);
    if (!parsed) {
      debug('el bloque trae un data-wrap-variant-id no válido:', raw);
      return null;
    }

    return parsed;
  }

  function rootUrl() {
    var root = '/';
    try {
      // `Theme` se declara con `const` en snippets/scripts.liquid: NO está en window.
      if (typeof Theme !== 'undefined' && Theme.routes) {
        root = Theme.routes.root_url || (Theme.routes.cart_url || '/cart').replace(/cart\/?$/, '') || '/';
      }
    } catch (error) {
      /* noop */
    }
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  /**
   * Carga (una sola vez) el id de la primera variante del envoltorio.
   * Se llama de forma perezosa, sólo cuando hace falta de verdad: el script
   * está en el layout y se ejecuta en TODAS las páginas del sitio.
   * Si el envoltorio no está disponible devolvemos null: preferimos no
   * añadirlo antes que hacer fallar el alta entera del producto principal.
   */
  function loadWrapVariantId() {
    if (wrapVariantId) return Promise.resolve(wrapVariantId);
    if (wrapVariantRequest) return wrapVariantRequest;

    wrapVariantRequest = originalFetch(rootUrl() + 'products/' + WRAP_HANDLE + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (response) {
        if (!response.ok) {
          debug('no se ha podido leer el producto del envoltorio: HTTP ' + response.status);
          return null;
        }
        return response.json();
      })
      .then(function (product) {
        var variant = product && product.variants && product.variants[0];
        if (!variant) {
          warn('No se ha encontrado el producto "' + WRAP_HANDLE + '".');
          debug('sin variante de envoltorio: el producto no existe o no tiene variantes');
          return null;
        }
        if (variant.available === false) {
          warn('El envoltorio no está disponible; no se añadirá al carrito.');
          debug('sin variante de envoltorio: la variante está agotada');
          return null;
        }
        wrapVariantId = variant.id;
        debug('variante de envoltorio cargada por handle:', wrapVariantId);
        return wrapVariantId;
      })
      .catch(function (error) {
        warn(error);
        debug('sin variante de envoltorio: error de red', error);
        wrapVariantRequest = null;
        return null;
      });

    return wrapVariantRequest;
  }

  /* ------------------------------------------------------------------ *
   * Utilidades
   * ------------------------------------------------------------------ */

  function toPositiveInt(value) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function isRequest(value) {
    return typeof Request !== 'undefined' && value instanceof Request;
  }

  /** Extrae la URL sea cual sea la forma del primer argumento de fetch. */
  function getUrlString(input) {
    if (typeof input === 'string') return input;
    if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
    if (isRequest(input)) return input.url;
    if (input && typeof input.url === 'string') return input.url;
    if (input && typeof input.toString === 'function') {
      try {
        return String(input);
      } catch (error) {
        return '';
      }
    }
    return '';
  }

  function withLoopFlag(url) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + LOOP_FLAG + '=1';
  }

  /**
   * Cantidad del formulario de producto, para el plan B (donde no podemos
   * deducirla del body). Si no la encontramos, 1.
   */
  function readFormQuantity() {
    try {
      var input = document.querySelector(QUANTITY_SELECTOR);
      return (input && toPositiveInt(input.value)) || 1;
    } catch (error) {
      return 1;
    }
  }

  /** Descripción legible del body, para los mensajes de depuración. */
  function describeBody(body) {
    if (body === undefined) return 'undefined';
    if (body === null) return 'null';
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return 'FormData con claves: [' + keysOf(body).join(', ') + ']';
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return 'URLSearchParams con claves: [' + keysOf(body).join(', ') + ']';
    }
    if (typeof body === 'string') return 'string: ' + body.slice(0, 300);
    if (typeof Blob !== 'undefined' && body instanceof Blob) return 'Blob';
    if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) return 'ArrayBuffer';
    return Object.prototype.toString.call(body);
  }

  /** Lista de claves de un FormData / URLSearchParams. */
  function keysOf(entries) {
    var keys = [];
    try {
      entries.forEach(function (value, key) {
        keys.push(key);
      });
    } catch (error) {
      /* noop */
    }
    return keys;
  }

  /** Normaliza Headers / array de pares / objeto plano a objeto plano. */
  function headersToObject(headers) {
    var out = {};
    if (!headers) return out;

    if (typeof Headers !== 'undefined' && headers instanceof Headers) {
      headers.forEach(function (value, key) {
        out[key] = value;
      });
      return out;
    }

    if (Array.isArray(headers)) {
      headers.forEach(function (pair) {
        if (pair && pair.length === 2) out[pair[0]] = pair[1];
      });
      return out;
    }

    if (typeof headers === 'object') {
      Object.keys(headers).forEach(function (key) {
        if (headers[key] !== undefined) out[key] = headers[key];
      });
    }

    return out;
  }

  function deleteHeader(headers, name) {
    var lower = name.toLowerCase();
    Object.keys(headers).forEach(function (key) {
      if (key.toLowerCase() === lower) delete headers[key];
    });
  }

  function hasHeader(headers, name) {
    var lower = name.toLowerCase();
    return Object.keys(headers).some(function (key) {
      return key.toLowerCase() === lower;
    });
  }

  /* ------------------------------------------------------------------ *
   * Construcción del payload
   * ------------------------------------------------------------------ */

  /**
   * FormData / URLSearchParams -> { payload, wrapQuantity }
   * Devuelve null si aparece cualquier campo que no sabemos traducir a JSON
   * (incluidos ficheros). Ojo: eso ya NO significa abandonar — el interceptor
   * se va al plan B.
   */
  function buildPayloadFromEntries(entries) {
    var main = { id: null, quantity: 1 };
    var top = {};
    var properties = {};
    var hasProperties = false;
    var quantity = null;
    var unsupported = false;
    var unsupportedKey = null;

    entries.forEach(function (rawValue, key) {
      if (unsupported) return;

      if (typeof File !== 'undefined' && rawValue instanceof File) {
        unsupported = true;
        unsupportedKey = key + ' (fichero adjunto)';
        return;
      }

      var value = String(rawValue);

      switch (key) {
        case 'id':
          if (value) main.id = value;
          return;
        case 'quantity': {
          var parsed = toPositiveInt(value);
          if (parsed) quantity = parsed;
          return;
        }
        case 'selling_plan':
          if (value) main.selling_plan = value;
          return;
        case 'sections':
          // product-form.js hace `append` dentro del bucle: nos quedamos
          // con el último valor no vacío, que es el más completo.
          if (value) top.sections = value;
          return;
        case 'sections_url':
          if (value) top.sections_url = value;
          return;
        case 'form_type':
        case 'utf8':
        case 'return_to':
        // Metadatos propios del formulario de Horizon 4.x: identifican el
        // producto y la seccion que lo pintan, no son propiedades de la
        // linea de carrito y /cart/add.js no los necesita en el payload.
        case 'product-id':
        case 'section-id':
          return;
        default: {
          var match = /^properties\[(.*)\]$/.exec(key);
          if (match) {
            properties[match[1]] = value;
            hasProperties = true;
            return;
          }
          // Radios de entrega de la tarjeta regalo (Horizon): control de UI
          // con nombre dinamico, no es propiedad de la linea de carrito.
          if (/^gift-card-delivery-/.test(key)) return;
          unsupported = true;
          unsupportedKey = key;
        }
      }
    });

    if (unsupported) {
      debug('clave desconocida en el formulario: ' + unsupportedKey);
      return null;
    }

    if (!main.id) {
      debug('el formulario no trae ninguna clave `id` con valor');
      return null;
    }

    main.quantity = quantity || 1;
    if (hasProperties) main.properties = properties;
    top.items = [main];

    return { payload: top, wrapQuantity: main.quantity };
  }

  /**
   * Objeto ya parseado desde un body JSON -> { payload, wrapQuantity }
   * Conserva intacto el resto del payload (sections, sections_url, etc.).
   */
  function buildPayloadFromJson(parsed) {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      debug('body JSON: no es un objeto');
      return null;
    }

    var payload = Object.assign({}, parsed);
    var items;

    if (Array.isArray(parsed.items)) {
      items = parsed.items.slice();
    } else if (parsed.id !== undefined && parsed.id !== null && parsed.id !== '') {
      // Forma "un solo ítem" de la AJAX API: la normalizamos a items[].
      var single = { id: parsed.id, quantity: toPositiveInt(parsed.quantity) || 1 };
      if (parsed.properties) single.properties = parsed.properties;
      if (parsed.selling_plan) single.selling_plan = parsed.selling_plan;
      items = [single];
      delete payload.id;
      delete payload.quantity;
      delete payload.properties;
      delete payload.selling_plan;
    } else {
      debug('body JSON: ni `items` ni `id`');
      return null;
    }

    if (!items.length) {
      debug('body JSON: `items` está vacío');
      return null;
    }

    var wrapQuantity = items.reduce(function (sum, item) {
      return sum + (toPositiveInt(item && item.quantity) || 1);
    }, 0);

    payload.items = items;

    return { payload: payload, wrapQuantity: wrapQuantity || 1 };
  }

  /**
   * Añade el envoltorio al array de items.
   * Devuelve siempre un objeto:
   *   { payload }                       -> listo para enviar
   *   { payload: null, alreadyPresent }  -> ya estaba: no hay nada que hacer
   *   null                               -> no hemos podido (plan B)
   */
  function appendWrapItem(result, variantId) {
    var items = result && result.payload && result.payload.items;

    if (!Array.isArray(items)) {
      debug('el payload construido no trae un array `items`');
      return null;
    }

    var alreadyPresent = items.some(function (item) {
      return item && String(item.id) === String(variantId);
    });
    if (alreadyPresent) {
      debug('envoltorio ya presente en items');
      return { payload: null, alreadyPresent: true };
    }

    items.push({ id: variantId, quantity: result.wrapQuantity });
    return { payload: result.payload, alreadyPresent: false };
  }

  /* ------------------------------------------------------------------ *
   * Lectura del body original
   * ------------------------------------------------------------------ */

  /**
   * Devuelve el body a inspeccionar. Si viene en un objeto Request lo leemos
   * de un clon, de forma que el Request original siga intacto y reutilizable.
   */
  async function readBody(input, init) {
    if (init && typeof init === 'object' && init.body !== undefined && init.body !== null) {
      return init.body;
    }

    if (isRequest(input)) {
      if (input.bodyUsed) return null;
      var clone = input.clone();
      var contentType = (input.headers.get('content-type') || '').toLowerCase();

      if (contentType.indexOf('multipart/form-data') !== -1 || contentType.indexOf('x-www-form-urlencoded') !== -1) {
        return await clone.formData();
      }

      return await clone.text();
    }

    return null;
  }

  /** body -> { payload, wrapQuantity } o null si el formato no se reconoce. */
  function analyzeBody(body) {
    if (!body) {
      debug('body vacío');
      return null;
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return buildPayloadFromEntries(body);
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return buildPayloadFromEntries(body);
    }

    if (typeof body === 'string') {
      var text = body.trim();
      if (!text) {
        debug('body string vacío');
        return null;
      }

      if (text.charAt(0) === '{') {
        var parsed;
        try {
          parsed = JSON.parse(text);
        } catch (error) {
          debug('body string: JSON inválido');
          return null;
        }
        return buildPayloadFromJson(parsed);
      }

      if (text.indexOf('=') !== -1 && typeof URLSearchParams !== 'undefined') {
        return buildPayloadFromEntries(new URLSearchParams(text));
      }

      debug('body string en un formato que no reconocemos');
      return null;
    }

    // Blob, ArrayBuffer, ReadableStream, etc.
    debug('body de un tipo binario o de stream que no sabemos leer');
    return null;
  }

  /* ------------------------------------------------------------------ *
   * Construcción de la petición modificada
   * ------------------------------------------------------------------ */

  function buildInit(input, init, bodyString) {
    var base = init && typeof init === 'object' ? init : {};
    var sourceIsRequest = isRequest(input);

    var headers = headersToObject(base.headers);
    if (!base.headers && sourceIsRequest) {
      headers = headersToObject(input.headers);
    }

    deleteHeader(headers, 'content-type');
    deleteHeader(headers, 'accept');
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    if (!hasHeader(headers, 'x-requested-with')) {
      headers['X-Requested-With'] = 'XMLHttpRequest';
    }

    var newInit = {
      method: base.method || (sourceIsRequest ? input.method : 'POST') || 'POST',
      headers: headers,
      body: bodyString,
    };

    ['credentials', 'mode', 'keepalive', 'signal'].forEach(function (key) {
      if (base[key] !== undefined) {
        newInit[key] = base[key];
      } else if (sourceIsRequest && input[key] !== undefined && input[key] !== null) {
        newInit[key] = input[key];
      }
    });

    return newInit;
  }

  /* ------------------------------------------------------------------ *
   * Plan B: dos peticiones
   * ------------------------------------------------------------------ */

  /**
   * Devuelve una copia de la respuesta SIN la clave `sections`.
   *
   * Tras el plan B el mini-carrito quedaría desactualizado: el HTML que Shopify
   * mete en `response.sections` se generó ANTES de añadir el envoltorio, y
   * product-form.js se lo pasa a cart-items-component, que lo morfea tal cual.
   * Si `sections` no viene, cart-items-component cae en su rama de reserva
   * (`sectionRenderer.renderSection(sectionId, { cache: false })`) y vuelve a
   * pedir la sección al servidor, ya con el envoltorio dentro. Es el mismo
   * mecanismo de refresco que usa el tema, sólo que forzado a repintar.
   * El contador de la cabecera ya se refresca solo: cart-icon lee el total del
   * `/cart.json` que product-form.js pide en su `#refreshCart()`.
   */
  async function stripSections(response) {
    try {
      var data = await response.clone().json();
      if (!data || typeof data !== 'object' || data.sections === undefined) return response;

      delete data.sections;

      var headers = new Headers(response.headers);
      headers.set('Content-Type', 'application/json');

      debug('quitamos `sections` de la respuesta para forzar el repintado del carrito');

      return new Response(JSON.stringify(data), {
        status: response.status,
        statusText: response.statusText,
        headers: headers,
      });
    } catch (error) {
      warn(error);
      debug('no hemos podido reescribir la respuesta; el mini-carrito puede quedar desactualizado');
      return response;
    }
  }

  /** Plan B: deja pasar la original y añade el envoltorio en una segunda petición. */
  async function addWrapSeparately(args, variantId, quantity) {
    var response = await originalFetch.apply(null, args);
    if (!response.ok) {
      // la venta manda: no insistimos
      debug('plan B abortado: la petición original ha respondido ' + response.status);
      return response;
    }
    try {
      var wrapResponse = await originalFetch(withLoopFlag(rootUrl() + 'cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] }),
      });
      if (!wrapResponse.ok) {
        debug('plan B: el alta del envoltorio ha respondido ' + wrapResponse.status);
        return response;
      }
      debug('envoltorio añadido por separado');
      document.dispatchEvent(new CustomEvent('lco:gift-wrap-added'));
      return await stripSections(response);
    } catch (error) {
      warn(error);
      debug('plan B: error al añadir el envoltorio', error);
      return response;
    }
  }

  /* ------------------------------------------------------------------ *
   * El interceptor
   * ------------------------------------------------------------------ */

  window.fetch = async function (input, init) {
    var args = arguments;

    try {
      var url = getUrlString(input);

      // No es un alta al carrito -> fuera.
      if (!url || url.indexOf(CART_ADD_MARKER) === -1) {
        // Muy ruidoso (salta en cada fetch de la página), pero es la única
        // forma de confirmar que el alta al carrito pasa por aquí.
        debug('no es /cart/add:', url || '(sin url)');
        return originalFetch.apply(this, args);
      }

      // Ya la hemos tocado nosotros (o alguien la ha reenviado) -> fuera.
      if (url.indexOf(LOOP_FLAG + '=1') !== -1) {
        debug('ya marcada con el flag:', url);
        return originalFetch.apply(this, args);
      }

      // El checkbox se busca AHORA: el bloque puede montarse después
      // que este script (se añade desde el editor de temas).
      var checkbox = document.querySelector(CHECKBOX_SELECTOR);
      if (!checkbox || !checkbox.checked) {
        debug('checkbox ausente o desmarcado', { presente: !!checkbox });
        return originalFetch.apply(this, args);
      }

      // 1) El bloque ya nos dice qué variante añadir -> cero peticiones de red.
      var variantId = readWrapVariantIdFromBlock(checkbox);
      if (variantId) {
        debug('variante de envoltorio leída del bloque:', variantId);
      } else {
        // 2) Bloque sin configurar -> resolvemos por handle (comportamiento previo).
        debug('el bloque no trae data-wrap-variant-id: resolvemos por handle');
        variantId = wrapVariantId || (await loadWrapVariantId());
        if (variantId) {
          debug('variante de envoltorio cargada por handle:', variantId);
        }
      }

      if (!variantId) {
        debug('sin variante de envoltorio: dejamos pasar la petición original');
        return originalFetch.apply(this, args);
      }

      var body = await readBody(input, init);
      var analysis = analyzeBody(body);

      // Plan B: no entendemos el body, pero sabemos qué queremos añadir.
      if (!analysis) {
        debug('body no reconocido -> plan B. ' + describeBody(body));
        return addWrapSeparately(args, variantId, readFormQuantity());
      }

      var appended = appendWrapItem(analysis, variantId);

      if (appended && appended.alreadyPresent) {
        // El envoltorio ya viaja en la petición: no hay nada que hacer.
        return originalFetch.apply(this, args);
      }

      if (!appended || !appended.payload) {
        debug('no hemos podido añadir el envoltorio al payload -> plan B');
        return addWrapSeparately(args, variantId, analysis.wrapQuantity);
      }

      var patchedInit = buildInit(input, init, JSON.stringify(appended.payload));
      var response = await originalFetch(withLoopFlag(url), patchedInit);

      // Red de seguridad: si el alta combinada falla (p. ej. el envoltorio se
      // ha quedado sin stock entre medias), reenviamos la petición original
      // tal cual y probamos el envoltorio aparte. /cart/add es atómico: si
      // falló, no se añadió nada.
      if (!response.ok) {
        warn('Alta con envoltorio rechazada (' + response.status + '). Reintentando por separado.');
        debug('respuesta no ok: ' + response.status + ' -> plan B');
        return addWrapSeparately(args, variantId, analysis.wrapQuantity);
      }

      debug('alta combinada correcta (producto + envoltorio en una sola petición)');
      return response;
    } catch (error) {
      warn(error);
      debug('error inesperado en el interceptor: dejamos pasar la original', error);
      return originalFetch.apply(this, args);
    }
  };

  debug('parche instalado');
})();
